export const GUIDE_META = {
  eyebrow: "Module Non-Conformité",
  title: "Guide utilisateur SMQ / NC",
  description:
    "Repères pratiques pour traiter, analyser et suivre une non-conformité.",
  standard: "Traitement des non-conformités et actions correctives — ISO 9001:2015",
};

export const GUIDE_SECTIONS = [
  {
    id: "presentation",
    number: "01",
    icon: "overview",
    title: "Présentation générale",
    introduction:
      "Le module Non-Conformité centralise le cycle de vie d'une NC : constat, qualification, traitement immédiat, analyse des causes, actions correctives et vérification d'efficacité.",
    cards: [
      {
        title: "Un dossier traçable",
        text: "Chaque enregistrement reçoit une référence et conserve ses informations, ses actions, ses suivis et son historique de réouverture.",
      },
      {
        title: "Un traitement partagé",
        text: "Les processus, directions, pilotes et responsables utilisent une même source de données et des statuts communs.",
      },
      {
        title: "Une lecture de pilotage",
        text: "Le registre, le tableau de bord et l'export Excel permettent de suivre les délais et les tendances.",
      },
    ],
    callouts: [
      {
        tone: "info",
        title: "Principe métier",
        text: "Une NC n'est clôturée qu'après vérification de l'efficacité des actions mises en œuvre. Une efficacité insuffisante entraîne une réouverture liée à la NC d'origine.",
      },
    ],
  },
  {
    id: "interface-navigation",
    number: "02",
    icon: "navigation",
    title: "Interface et navigation",
    introduction:
      "Le menu latéral donne accès aux parcours opérationnels. Sur tablette et mobile, le bouton de menu ouvre la navigation et celle-ci se referme après la sélection d'un écran.",
    tables: [
      {
        title: "Repères du menu",
        headers: ["Menu", "Utilisation"],
        rows: [
          ["Tableau de bord", "Vue synthétique des volumes, statuts, délais et actions."],
          ["Nouvelle NC", "Création et traitement progressif d'une non-conformité."],
          ["Registre des NC", "Recherche, consultation, modification autorisée et filiation."],
          ["Analyser NC", "Accès rapide aux dossiers dont l'analyse doit être complétée."],
          ["NC rouvertes", "Consultation des NC d'origine et de leurs sous-NC."],
          ["Suivi efficacité", "Évaluation de l'efficacité et décision de clôture ou de réouverture."],
          ["Données", "Export Excel et, pour les administrateurs, accès à la corbeille."],
          ["Paramétrage", "Référentiels métier accessibles selon les privilèges."],
        ],
      },
      {
        title: "Statuts d'une NC",
        headers: ["Libellé", "Signification"],
        rows: [
          ["Ouverte", "Le dossier est créé et son traitement reste à compléter."],
          ["En cours", "Des traitements ou actions sont engagés."],
          ["Rouverte", "La NC d'origine a généré une sous-NC après une efficacité insuffisante."],
          ["Clôturée", "L'efficacité a été confirmée et le dossier est terminé."],
        ],
      },
    ],
    callouts: [
      {
        tone: "tip",
        title: "Accès",
        text: "Les menus et actions visibles dépendent de vos privilèges. Les opérations d'administration peuvent donc ne pas apparaître dans votre interface.",
      },
    ],
  },
  {
    id: "saisie-nc",
    number: "03",
    icon: "edit",
    title: "Saisir une non-conformité",
    introduction:
      "Le formulaire organise le traitement en cinq étapes. Une fois le constat enregistré, la barre d'étapes permet de revenir librement sur les écrans autorisés sans perdre les données chargées.",
    steps: [
      {
        title: "Constat",
        text: "Choisissez le déclencheur, le processus et la direction, puis renseignez le déclarant, la date et la description factuelle de l'écart. La référence est générée à partir du déclencheur et reste inchangée après création.",
      },
      {
        title: "Ouverture",
        text: "Précisez l'exigence concernée, qualifiez l'écart, évaluez gravité et fréquence, puis désignez le pilote et son échéance.",
      },
      {
        title: "Action curative",
        text: "Décrivez la mesure immédiate qui limite l'effet de la NC, son responsable, sa date et son état de réalisation.",
      },
      {
        title: "Analyse des causes",
        text: "Partez du problème constaté, enchaînez les Pourquoi, associez les familles 5M pertinentes et formulez la cause racine.",
      },
      {
        title: "Actions correctives",
        text: "Définissez une ou plusieurs actions qui éliminent la cause racine, avec plan, indicateur, avancement, dates, délai et responsable.",
      },
      {
        title: "Suivi d'efficacité",
        text: "Dans le menu dédié, vérifiez les résultats obtenus. Une décision positive clôture la NC ; une décision négative crée une sous-NC rattachée à l'origine.",
      },
    ],
    callouts: [
      {
        tone: "warning",
        title: "Avant de poursuivre",
        text: "Les champs obligatoires sont contrôlés à chaque enregistrement. Les erreurs apparaissent sous les champs concernés et l'étape reste affichée tant que les informations sont incomplètes.",
      },
      {
        tone: "info",
        title: "Actions non validées individuellement",
        text: "Lors de l'enregistrement de la NC, les actions correctives complètement renseignées peuvent être sauvegardées en attente de confirmation. Le bouton individuel Valider reste disponible pour confirmer une action.",
      },
    ],
  },
  {
    id: "registre-filtres",
    number: "04",
    icon: "register",
    title: "Registre et filtres",
    introduction:
      "Le registre réunit les NC et combine les familles réouvertes afin de conserver une lecture centrée sur le dossier d'origine.",
    tables: [
      {
        title: "Filtres disponibles",
        headers: ["Filtre", "Effet"],
        rows: [
          ["Recherche", "Recherche dans les références et les informations textuelles affichées."],
          ["Processus", "Limite la liste au processus sélectionné."],
          ["Exigence", "Limite la liste à l'exigence concernée."],
          ["Criticité", "Affiche les NC mineures, majeures ou critiques."],
          ["Statut", "Filtre les NC ouvertes, en cours, rouvertes ou clôturées."],
          ["Du / Au", "Applique une période inclusive sur la date du constat ; une seule borne peut être utilisée."],
        ],
      },
      {
        title: "Actions du registre",
        headers: ["Action", "Comportement"],
        rows: [
          ["Œil ou double-clic", "Ouvre le dossier complet, en lecture seule, des étapes 1 à 6."],
          ["Filiation", "Affiche l'origine, la sous-NC et l'historique familial associé."],
          ["Modifier", "Ouvre le dossier encore modifiable ; une ligne combinée cible la sous-NC active."],
          ["Télécharger Excel", "Exporte exactement les lignes correspondant aux filtres actifs."],
          ["Masquer les filtres", "Masque les champs sans effacer les valeurs appliquées."],
          ["Réinitialiser", "Efface la recherche, les listes et les deux dates."],
        ],
      },
    ],
    callouts: [
      {
        tone: "tip",
        title: "Lecture des familles réouvertes",
        text: "La description affichée reste celle de la NC d'origine. Le statut indique qu'une action a été jugée insuffisante et la modification ouvre la sous-NC active.",
      },
    ],
  },
  {
    id: "analyse-causes",
    number: "05",
    icon: "analysis",
    title: "Analyse des causes",
    introduction:
      "L'analyse combine la méthode des Pourquoi et les familles 5M. Le problème de départ reprend la description du constat lorsqu'aucune analyse n'existe encore, puis reste entièrement modifiable.",
    steps: [
      {
        title: "Décrire le problème de départ",
        text: "Utilisez une formulation observable, précise et sans supposer la cause.",
      },
      {
        title: "Ajouter les Pourquoi",
        text: "Pour chaque réponse, posez à nouveau la question jusqu'à atteindre une cause sur laquelle l'organisation peut agir.",
      },
      {
        title: "Associer les familles 5M",
        text: "Une même réponse peut relever de plusieurs familles. Sélectionnez toutes celles qui apportent une lecture utile.",
      },
      {
        title: "Conclure par la cause racine",
        text: "Synthétisez la ou les causes démontrées ; les actions correctives devront les traiter directement.",
      },
    ],
    tables: [
      {
        title: "Les familles 5M",
        headers: ["Famille", "Points à examiner"],
        rows: [
          ["Milieu", "Environnement, organisation des espaces, contraintes et conditions de travail."],
          ["Main-d'œuvre", "Compétences, effectifs, formation, responsabilités et communication."],
          ["Matériel", "Équipements, outils, logiciels, maintenance et disponibilité."],
          ["Matières", "Données d'entrée, documents, fournitures et qualité des intrants."],
          ["Méthodes", "Procédures, consignes, contrôles, planification et modes opératoires."],
        ],
      },
    ],
    callouts: [
      {
        tone: "warning",
        title: "Cause ou symptôme ?",
        text: "Une reformulation de l'écart n'est pas une cause racine. Cherchez un facteur explicatif vérifiable, maîtrisable et suffisamment précis pour définir une action.",
      },
    ],
  },
  {
    id: "tableau-bord",
    number: "06",
    icon: "dashboard",
    title: "Tableau de bord",
    introduction:
      "Le tableau de bord consolide les NC racines. Les sous-NC ne sont pas recomptées : une famille parent/enfants représente une seule NC dans les indicateurs.",
    tables: [
      {
        title: "Indicateurs principaux",
        headers: ["Indicateur", "Lecture recommandée"],
        rows: [
          ["NC enregistrées", "Volume des NC racines correspondant aux filtres actifs."],
          ["Ouvertes", "Dossiers créés dont le traitement reste à engager ou compléter."],
          ["En cours", "Dossiers disposant déjà d'éléments de traitement."],
          ["Clôturées", "Dossiers dont l'efficacité a été confirmée."],
          ["Échéances proches", "Dossiers à traiter prochainement ; anticiper la relance."],
          ["Délais dépassés", "Dossiers non clôturés au-delà de l'échéance ; action prioritaire."],
          ["Curatif / Correctif en cours", "Actions des seules NC racines, selon leur état d'avancement."],
        ],
      },
    ],
    callouts: [
      {
        tone: "info",
        title: "Filtres du tableau de bord",
        text: "Processus, direction et période s'appliquent ensemble aux cartes, graphiques et listes détaillées. Réinitialisez-les pour revenir à la vue globale.",
      },
    ],
  },
  {
    id: "enregistrement-conservation",
    number: "07",
    icon: "save",
    title: "Enregistrement et conservation",
    introduction:
      "Les données sont enregistrées dans l'application et non dans le stockage local du navigateur. Fermer l'écran n'efface pas une étape déjà sauvegardée.",
    cards: [
      {
        title: "Enregistrer à chaque étape",
        text: "Utilisez les boutons du formulaire et attendez le message de confirmation avant de quitter ou de changer de page.",
      },
      {
        title: "Conserver la référence",
        text: "La référence et l'index créés avec le constat sont historiques et restent inchangés lors d'une modification.",
      },
      {
        title: "Tracer une réouverture",
        text: "Une sous-NC possède sa propre référence tout en restant consultable avec son origine et les autres dossiers liés.",
      },
    ],
    callouts: [
      {
        tone: "warning",
        title: "Éviter les doublons",
        text: "Pendant un enregistrement ou une redirection, les boutons sont temporairement désactivés. Ne rechargez pas la page tant que l'opération n'est pas terminée.",
      },
      {
        tone: "tip",
        title: "Suppression",
        text: "La mise à la corbeille, la restauration et les opérations définitives sont réservées aux administrateurs SMQ. Une suppression logique préserve la traçabilité prévue par l'application.",
      },
    ],
  },
  {
    id: "export-impression",
    number: "08",
    icon: "export",
    title: "Export Excel et impression",
    introduction:
      "Le module propose un export consolidé et une impression ciblée des dossiers consultés.",
    tables: [
      {
        title: "Contenu de l'export Excel",
        headers: ["Feuille", "Contenu", "Usage"],
        rows: [
          ["Registre NC", "Synthèse des références, dates, processus, criticités, statuts et délais.", "Pilotage et reporting."],
          ["Fiches complètes", "Informations détaillées disponibles pour chaque NC.", "Audit et archivage."],
          ["Analyse 5P 5M", "Une ligne par Pourquoi avec problème de départ, familles 5M et cause racine.", "Analyse des causes."],
          ["Plan d'actions", "Actions, responsables, échéances, indicateurs et avancement.", "Suivi opérationnel."],
        ],
      },
    ],
    steps: [
      {
        title: "Exporter le registre filtré",
        text: "Dans le Registre, appliquez vos critères puis utilisez Télécharger Excel : seules les lignes filtrées sont exportées.",
      },
      {
        title: "Produire l'export complet",
        text: "Dans le menu Exporter Excel, lancez la génération et attendez la fin du chargement des détails.",
      },
      {
        title: "Imprimer une fiche",
        text: "Ouvrez le détail d'une NC puis cliquez sur Imprimer. Le rendu masque la navigation et affiche l'ensemble du dossier consulté.",
      },
    ],
  },
  {
    id: "matrice-criticite",
    number: "09",
    icon: "matrix",
    title: "Matrice de criticité",
    introduction:
      "La criticité est déterminée à l'ouverture de la NC en croisant la gravité de l'impact et la fréquence ou probabilité d'occurrence.",
    tables: [
      {
        title: "Échelles d'évaluation",
        headers: ["Note", "Gravité / impact", "Fréquence / probabilité"],
        rows: [
          ["1", "Sans impact opérationnel significatif.", "Jamais ou aucun antécédent connu."],
          ["2", "Impact possible sur le processus.", "Rare, environ une fois dans l'année."],
          ["3", "Impact réel ou non-respect d'un indicateur.", "Prévisible, plusieurs occurrences possibles par an."],
          ["4", "Impact légal, contractuel ou sur les bénéficiaires.", "Récurrent, notamment sur les trois derniers mois."],
        ],
      },
      {
        title: "Lecture directe de la matrice",
        headers: ["Gravité \\ Fréquence", "1", "2", "3", "4"],
        rows: [
          ["1", "Mineure", "Mineure", "Mineure", "Mineure"],
          ["2", "Mineure", "Mineure", "Mineure", "Majeure"],
          ["3", "Majeure", "Majeure", "Majeure", "Critique"],
          ["4", "Critique", "Critique", "Critique", "Critique"],
        ],
      },
      {
        title: "Niveaux et priorité",
        headers: ["Niveau", "Lecture", "Délai indicatif"],
        rows: [
          ["Mineure", "Impact limité ou maîtrisable.", "Traitement dans la semaine."],
          ["Majeure", "Impact important nécessitant une mobilisation rapide.", "Traitement sous 72 heures."],
          ["Critique", "Impact légal, contractuel ou bénéficiaire majeur.", "Traitement sous 48 heures, immédiatement si nécessaire."],
        ],
      },
    ],
    callouts: [
      {
        tone: "warning",
        title: "Évaluer avec des faits",
        text: "Justifiez les notes à partir des impacts observés, des données disponibles et des occurrences réelles. La matrice guide la priorité ; elle ne remplace pas le jugement métier.",
      },
    ],
  },
  // {
  //   id: "communication",
  //   number: "10",
  //   icon: "communication",
  //   title: "Règles de communication",
  //   introduction:
  //     "La communication commence dès la qualification de la NC. Adaptez les destinataires, le canal et la rapidité au niveau de criticité et aux règles internes en vigueur.",
  //   tables: [
  //     {
  //       title: "Circuit recommandé",
  //       headers: ["Criticité", "Destinataires minimum"],
  //       rows: [
  //         ["Mineure", "Responsable direct et pilote de la NC."],
  //         ["Majeure", "Responsable direct, pilote ou responsable de processus et Leader SMQ."],
  //         ["Critique", "Responsables, Leader SMQ, direction et partenaires concernés selon l'impact."],
  //       ],
  //     },
  //   ],
  //   bulletGroups: [
  //     {
  //       title: "Une notification utile précise au minimum",
  //       items: [
  //         "la référence, la date et le processus concerné ;",
  //         "le constat factuel, l'exigence et la criticité ;",
  //         "la mesure curative immédiate et le pilote ;",
  //         "l'échéance, les prochaines actions et les destinataires attendus.",
  //       ],
  //     },
  //     {
  //       title: "À la clôture",
  //       items: [
  //         "informer les mêmes parties prenantes que lors de l'ouverture ;",
  //         "rappeler les actions réalisées et la preuve d'efficacité ;",
  //         "indiquer la décision de clôture ou la création d'une sous-NC en cas d'insuffisance.",
  //       ],
  //     },
  //   ],
  // },
  {
    id: "faq-bonnes-pratiques",
    number: "11",
    icon: "faq",
    title: "FAQ et bonnes pratiques",
    introduction:
      "Les réponses ci-dessous décrivent le fonctionnement de l'application actuelle.",
    faqs: [
      {
        question: "Puis-je modifier une NC enregistrée ?",
        answer: "Oui, tant que son statut et vos droits autorisent la modification. Une NC clôturée ou une origine déjà marquée comme rouverte reste consultative.",
      },
      {
        question: "Pourquoi la référence ne change-t-elle pas après modification ?",
        answer: "La référence et l'index sont des identifiants historiques. Modifier la date ou le déclencheur n'altère pas la référence déjà attribuée.",
      },
      {
        question: "Que signifie une NC rouverte ?",
        answer: "La vérification d'efficacité du dossier d'origine a conclu que l'action était insuffisante. Une sous-NC est alors créée et reliée à cette origine.",
      },
      {
        question: "Puis-je retrouver l'origine d'une sous-NC ?",
        answer: "Oui. Dans le Registre, utilisez l'icône de filiation pour consulter l'origine, la sous-NC et l'historique familial disponible.",
      },
      {
        question: "Pourquoi certaines actions ne sont-elles pas visibles ?",
        answer: "Les actions sensibles et les paramètres sont réservés aux utilisateurs possédant les privilèges administrateur SMQ.",
      },
      {
        question: "Les filtres sont-ils perdus lorsque je les masque ?",
        answer: "Non. Masquer les filtres conserve leurs valeurs actives. Utilisez Réinitialiser pour réellement les effacer.",
      },
    ],
    bulletGroups: [
      {
        title: "Bonnes pratiques de saisie",
        items: [
          "décrire des faits datés et vérifiables, sans jugement sur les personnes ;",
          "lier chaque action corrective à une cause racine démontrée ;",
          "désigner un responsable unique et une échéance réaliste ;",
          "choisir un indicateur mesurable avant de lancer l'action ;",
          "joindre ou référencer les preuves utiles au suivi ;",
          "réaliser la vérification d'efficacité après un délai suffisant pour observer le résultat.",
        ],
      },
    ],
    callouts: [
      {
        tone: "tip",
        title: "Besoin d'aide métier ?",
        text: "Contactez le responsable SMQ ou le pilote du processus lorsque la qualification, la criticité ou la décision de clôture nécessite une validation.",
      },
    ],
  },
];

